import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { summarizeStorePackages } from "../utils/storePackages";

const UI_CURRENCY = "LKR";
const n = (value) => Number(value || 0);

function money(value) {
  return `${UI_CURRENCY} ${n(value).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(value) {
  const date = value?.toDate?.() || value;
  if (!date) return "-";
  try {
    return new Date(date).toLocaleString();
  } catch {
    return "-";
  }
}

function getOwnerName(user = {}) {
  return (
    user.fullName ||
    user.displayName ||
    user.name ||
    user.email ||
    user.uid ||
    "Unknown shop owner"
  );
}

function getOwnerStatusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "approved") return "bg-green-100 text-green-700";
  if (value === "declined") return "bg-rose-100 text-rose-700";
  if (value === "pending") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function guessShopOwners(users = [], listingMap = {}, packageMap = {}) {
  return users.filter((user) => {
    const uid = user.id;
    const hasActivePackages =
      Array.isArray(user.activeStorePackages) &&
      user.activeStorePackages.length > 0;
    return (
      String(user.role || "").toLowerCase() === "instructor" ||
      user.hasStorePackage === true ||
      hasActivePackages ||
      Boolean(user.storePackage?.packageId) ||
      Boolean(user.storeBusinessType) ||
      Boolean(listingMap[uid]) ||
      Boolean(packageMap[uid])
    );
  });
}

function StatCard({ label, value, helper = "", tone = "text-slate-900" }) {
  return (
    <div className="rounded-3xl border bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </div>
      <div className={`mt-3 text-3xl font-black ${tone}`}>{value}</div>
      {helper ? <div className="mt-2 text-sm text-slate-500">{helper}</div> : null}
    </div>
  );
}

export default function ShopOwnersSummary() {
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [packageOrders, setPackageOrders] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "courses"), (snap) => {
      setListings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "instructor_package_orders"), orderBy("createdAt", "desc")),
      (snap) => setPackageOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    return () => unsub();
  }, []);

  const listingMap = useMemo(() => {
    return listings.reduce((acc, row) => {
      const uid = row.createdBy;
      if (!uid) return acc;
      const entry = acc[uid] || {
        total: 0,
        approved: 0,
        pending: 0,
        declined: 0,
        course: 0,
        product: 0,
        service: 0,
      };
      entry.total += 1;
      const type = String(row.type || "").toLowerCase();
      if (type === "product") entry.product += 1;
      else if (type === "service") entry.service += 1;
      else entry.course += 1;
      const status = String(row.status || "").toLowerCase();
      if (status === "approved") entry.approved += 1;
      else if (status === "declined") entry.declined += 1;
      else entry.pending += 1;
      acc[uid] = entry;
      return acc;
    }, {});
  }, [listings]);

  const packageMap = useMemo(() => {
    return packageOrders.reduce((acc, row) => {
      const uid = row.instructorId;
      if (!uid) return acc;
      const entry = acc[uid] || {
        totalOrders: 0,
        approvedOrders: 0,
        pendingOrders: 0,
        declinedOrders: 0,
        revenue: 0,
        affiliateIncome: 0,
        developerIncome: 0,
        lastPackageAt: null,
      };
      entry.totalOrders += 1;
      const status = String(row.status || "").toLowerCase();
      if (status === "approved") {
        entry.approvedOrders += 1;
        entry.revenue += n(row.packageSnapshot?.price);
        entry.affiliateIncome += n(
          row.affiliateIncomeAmount ?? row.payoutSnapshot?.affiliatePayoutApplied,
        );
        entry.developerIncome += n(
          row.developerIncomeAmount ?? row.payoutSnapshot?.developerPayoutApplied,
        );
      } else if (status === "declined") {
        entry.declinedOrders += 1;
      } else {
        entry.pendingOrders += 1;
      }
      if (!entry.lastPackageAt && row.createdAt) entry.lastPackageAt = row.createdAt;
      acc[uid] = entry;
      return acc;
    }, {});
  }, [packageOrders]);

  const allOwners = useMemo(
    () => guessShopOwners(users, listingMap, packageMap),
    [users, listingMap, packageMap],
  );

  const filteredOwners = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = allOwners
      .map((user) => {
        const packageState = summarizeStorePackages(user);
        return {
          user,
          packageState,
          listingSummary: listingMap[user.id] || {
            total: 0,
            approved: 0,
            pending: 0,
            declined: 0,
            course: 0,
            product: 0,
            service: 0,
          },
          packageSummary: packageMap[user.id] || {
            totalOrders: 0,
            approvedOrders: 0,
            pendingOrders: 0,
            declinedOrders: 0,
            revenue: 0,
            affiliateIncome: 0,
            developerIncome: 0,
            lastPackageAt: null,
          },
        };
      })
      .sort(
        (a, b) =>
          n(b.packageSummary.revenue) - n(a.packageSummary.revenue) ||
          b.listingSummary.total - a.listingSummary.total,
      );

    if (!q) return rows;

    return rows.filter(({ user }) =>
      [
        user.id,
        user.uid,
        user.email,
        user.fullName,
        user.displayName,
        user.name,
        user.storeBusinessType,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [allOwners, listingMap, packageMap, search]);

  const stats = useMemo(() => {
    return filteredOwners.reduce(
      (acc, row) => {
        acc.totalOwners += 1;
        if (String(row.user.status || "").toLowerCase() === "approved") {
          acc.approvedOwners += 1;
        }
        if (row.packageState.hasActivePackage) acc.activePackageOwners += 1;
        acc.totalListings += row.listingSummary.total;
        acc.packageRevenue += row.packageSummary.revenue;
        acc.affiliateIncome += row.packageSummary.affiliateIncome;
        return acc;
      },
      {
        totalOwners: 0,
        approvedOwners: 0,
        activePackageOwners: 0,
        totalListings: 0,
        packageRevenue: 0,
        affiliateIncome: 0,
      },
    );
  }, [filteredOwners]);

  return (
    <div className="space-y-6 p-6">
      <div className="rounded-[32px] border border-sky-200 bg-gradient-to-br from-[#f4fbff] via-white to-[#eef4ff] p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex rounded-full border border-sky-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
              Shop Owners
            </div>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              All shop owners with package, listing, and income summaries
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
              Review each shop owner in one place, including package usage, listing counts, package sales volume, and the referral income generated through their package purchases.
            </p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search shop owner, email, UID..."
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none ring-0 lg:w-80"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Shop Owners" value={stats.totalOwners} />
        <StatCard label="Approved Owners" value={stats.approvedOwners} />
        <StatCard label="With Active Package" value={stats.activePackageOwners} />
        <StatCard label="Total Listings" value={stats.totalListings} />
        <StatCard label="Affiliate Income" value={money(stats.affiliateIncome)} tone="text-emerald-700" helper={`Package revenue ${money(stats.packageRevenue)}`} />
      </div>

      <div className="rounded-3xl border bg-white p-5 shadow-sm">
        <h2 className="text-xl font-black text-slate-900">Shop Owner Summary Table</h2>
        <p className="mt-1 text-sm text-slate-500">
          Each row shows current package capacity, listings posted, and package purchase totals.
        </p>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[1280px] text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="px-3 py-3 font-semibold">Shop Owner</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Store Mode</th>
                <th className="px-3 py-3 font-semibold">Active Package</th>
                <th className="px-3 py-3 font-semibold">Limits</th>
                <th className="px-3 py-3 font-semibold">Listings</th>
                <th className="px-3 py-3 font-semibold text-right">Pkg Revenue</th>
                <th className="px-3 py-3 font-semibold text-right">Affiliate Income</th>
                <th className="px-3 py-3 font-semibold text-right">Developer Income</th>
                <th className="px-3 py-3 font-semibold">Last Package</th>
              </tr>
            </thead>
            <tbody>
              {filteredOwners.map(({ user, packageState, listingSummary, packageSummary }) => (
                <tr key={user.id} className="border-b last:border-b-0">
                  <td className="px-3 py-3 align-top">
                    <div className="font-semibold text-slate-900">{getOwnerName(user)}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {user.email || user.id}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getOwnerStatusTone(user.status)}`}>
                      {user.status || "unknown"}
                    </span>
                  </td>
                  <td className="px-3 py-3 align-top">
                    {user.storeBusinessType || "Not selected"}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="font-semibold text-slate-900">
                      {packageState.summary.name || "No package"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {packageState.hasActivePackage
                        ? `${packageState.summary.totalPackages} active`
                        : "Inactive"}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top text-slate-600">
                    C {n(packageState.summary.courseLimit) || 0} • P{" "}
                    {n(packageState.summary.productLimit) || 0} • S{" "}
                    {n(packageState.summary.serviceLimit) || 0}
                  </td>
                  <td className="px-3 py-3 align-top text-slate-600">
                    {listingSummary.total} total
                    <div className="mt-1 text-xs text-slate-500">
                      C {listingSummary.course} • P {listingSummary.product} • S {listingSummary.service}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-slate-900">
                    {money(packageSummary.revenue)}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-emerald-700">
                    {money(packageSummary.affiliateIncome)}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-indigo-700">
                    {money(packageSummary.developerIncome)}
                  </td>
                  <td className="px-3 py-3 align-top text-slate-500">
                    {fmtDate(packageSummary.lastPackageAt)}
                  </td>
                </tr>
              ))}
              {filteredOwners.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                    No shop owners matched your search.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
